import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, first, Subscription, tap } from 'rxjs';
import { getRouterOutletState } from '../shared/helpers/utility';
import { VehicleRealTimeService } from '../shared/vehicle/services/real-time.service';
import { selectVehicles } from '../shared/vehicle/store/vehicle.selectors';
import { LiveDataService } from './pages/live/live-data.service';
import { VehicleDictionary, VehicleStateful } from './pages/live/live.models';

@Component({
  selector: 'app-monitoring',
  templateUrl: './monitoring.component.html',
  styleUrls: ['./monitoring.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MonitoringComponent implements OnInit, OnDestroy {
  private readonly _rxSubscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly realTimeService: VehicleRealTimeService,
    private readonly live: LiveDataService
  ) {}

  ngOnInit(): void {
    const initializeRealtime$ = combineLatest([
      this.store.select(selectVehicles),
      this.realTimeService.getCachedSnapshots(),
    ])
      .pipe(
        first(([vehicles, snapshots]) => vehicles.value !== undefined && snapshots !== undefined),
        tap(([vehicles, snapshots]) => {
          const vehicleDict: VehicleDictionary = {};
          for (const vehicle of vehicles.value ?? [])
            vehicleDict[vehicle.vin] = VehicleStateful.from(vehicle, snapshots[vehicle.vin]);

          this.live.vehicles = vehicleDict;

          const stateSnapshots$ = this.realTimeService.snapshots$.subscribe(data =>
            this.live.updateVehicleStates(data)
          );
          this._rxSubscription.add(stateSnapshots$);

          const alertSnapshots$ = this.realTimeService.alerts$.subscribe(alerts =>
            this.live.updateVehicleAlerts(alerts)
          );
          this._rxSubscription.add(alertSnapshots$);

          this.realTimeService.start();
        })
      )
      .subscribe();

    this._rxSubscription.add(initializeRealtime$);
  }

  ngOnDestroy(): void {
    this._rxSubscription.unsubscribe();
    this.realTimeService.clear();
    this.live.vehicles = {};
    this.live.vehicleVINs = null;
    this.live.selectedVIN = null;
    this.live.followSelected = false;
  }

  getState = getRouterOutletState;
}
